(() => {
  const SELECTOR = "[data-native-surprise-popup]";
  const STATES = ["reveal", "signup", "success"];
  const STEP_LABELS = {
    reveal: "01 / 03",
    signup: "02 / 03",
    success: "03 / 03",
  };

  const getParams = () => new URLSearchParams(window.location.search);
  const isPreviewRequest = (root) => {
    const params = getParams();
    return params.get("native_popup") === "1" || window.Shopify?.designMode || root.dataset.previewOnly !== "true";
  };

  const readStoredUntil = (key) => {
    try {
      return Number(window.localStorage.getItem(key) || 0);
    } catch (_error) {
      return 0;
    }
  };

  const writeStoredUntil = (key, days) => {
    try {
      const until = Date.now() + Number(days || 30) * 24 * 60 * 60 * 1000;
      window.localStorage.setItem(key, String(until));
    } catch (_error) {
      /* Storage can be unavailable in private browsing; popup still closes for the session. */
    }
  };

  const readStoredStatus = (key) => {
    try {
      return window.localStorage.getItem(key) || "";
    } catch (_error) {
      return "";
    }
  };

  const writeStoredStatus = (key, status) => {
    try {
      window.localStorage.setItem(key, status);
    } catch (_error) {
      /* Storage can be unavailable in private browsing. */
    }
  };

  const focusableSelector = [
    "button:not([disabled])",
    "input:not([disabled])",
    "a[href]",
    "select:not([disabled])",
    "textarea:not([disabled])",
    "[tabindex]:not([tabindex='-1'])",
  ].join(",");

  class NativeSurprisePopup {
    constructor(root) {
      this.root = root;
      this.dialog = root.querySelector("[data-ii-surprise-dialog]");
      this.form = root.querySelector(".ii-surprise__form");
      this.emailInput = root.querySelector("[data-ii-surprise-email]");
      this.emailError = root.querySelector("[data-ii-surprise-email-error]");
      this.stepCount = root.querySelector("[data-ii-surprise-step-count]");
      this.imagePanel = root.querySelector("[data-ii-surprise-image-panel]");
      this.lifestyleImage = root.querySelector("[data-ii-surprise-lifestyle-image]");
      this.resultSignal = root.querySelector("[data-ii-surprise-form-result]");
      this.launcher = document.querySelector(`[data-ii-surprise-launcher="${root.dataset.sectionId}"]`);
      this.successHeading = root.querySelector("[data-ii-surprise-success-heading]");
      this.successSupport = root.querySelector("[data-ii-surprise-success-support]");
      this.activeState = "reveal";
      this.previouslyFocused = null;
      this.storageKey = `ii-native-surprise-popup-${root.dataset.sectionId}`;
      this.statusKey = `${this.storageKey}-status`;
      this.bodyOverflow = "";
      this.boundKeydown = this.onKeydown.bind(this);
      this.boundFocusIn = this.onFocusIn.bind(this);
    }

    init() {
      this.root.querySelector("[data-ii-surprise-reveal]")?.addEventListener("click", () => this.setState("signup"));
      this.launcher?.addEventListener("click", () => this.open("reveal"));
      this.root.querySelectorAll("[data-ii-surprise-close]").forEach((button) => {
        button.addEventListener("click", () => this.close(true));
      });
      this.root.querySelector("[data-ii-surprise-shop]")?.addEventListener("click", () => this.close(true));
      this.root.addEventListener("mousedown", (event) => {
        if (event.target === this.root.querySelector(".ii-surprise__backdrop")) {
          this.close(true);
        }
      });
      this.form?.addEventListener("submit", this.onSubmit.bind(this));
      this.lifestyleImage?.addEventListener("error", () => this.failImage(this.lifestyleImage, this.imagePanel));
      this.root.querySelectorAll("[data-ii-surprise-logo]").forEach((logo) => {
        logo.addEventListener("error", () => this.failLogo(logo));
      });

      const params = getParams();
      if (params.get("native_popup_images") === "off") {
        this.failImage(this.lifestyleImage, this.imagePanel);
        this.root.querySelectorAll("[data-ii-surprise-logo]").forEach((logo) => this.failLogo(logo));
      }

      const requestedResult = this.root.dataset.testMode === "true" ? params.get("native_popup_result") : null;
      const serverResult = this.resultSignal?.dataset.iiSurpriseFormResult || "";
      const result = ["success", "already", "error"].includes(requestedResult) ? requestedResult : serverResult;

      if (["success", "already", "error"].includes(result)) {
        window.setTimeout(() => {
          this.open(result === "error" ? "signup" : "success");
          this.applyFormResult(result, this.resultSignal?.dataset.message || "");
        }, 0);
        return;
      }

      if (!isPreviewRequest(this.root)) return;
      if (this.root.dataset.previewOnly !== "true" && readStoredUntil(this.storageKey) > Date.now()) {
        if (readStoredStatus(this.statusKey) === "dismissed") this.showLauncher();
        return;
      }

      const requestedState = params.get("native_popup_state");
      const delay = params.get("native_popup") === "1" ? 0 : Number(this.root.dataset.showDelay || 0) * 1000;

      window.setTimeout(() => {
        this.open(STATES.includes(requestedState) ? requestedState : "reveal");
      }, delay);
    }

    setState(nextState) {
      this.activeState = nextState;
      this.dialog.dataset.iiSurpriseActiveState = nextState;
      if (this.stepCount) this.stepCount.textContent = STEP_LABELS[nextState];

      this.root.querySelectorAll("[data-ii-surprise-dot]").forEach((dot) => {
        dot.classList.toggle("is-active", dot.dataset.iiSurpriseDot === nextState);
      });

      this.root.querySelectorAll("[data-ii-surprise-state]").forEach((panel) => {
        const active = panel.dataset.iiSurpriseState === nextState;
        panel.classList.toggle("is-active", active);
        panel.setAttribute("aria-hidden", String(!active));
        panel.querySelector("h2")?.removeAttribute("id");
      });

      const heading = this.root.querySelector(`[data-ii-surprise-state="${nextState}"] h2`);
      heading?.setAttribute("id", `ii-surprise-title-${this.root.dataset.sectionId}`);

      window.setTimeout(() => {
        const target = nextState === "signup" ? this.emailInput : this.root.querySelector(`[data-ii-surprise-state="${nextState}"] button`);
        if (!this.isPrivacyDialogOpen()) target?.focus();
      }, 0);
    }

    open(initialState = "reveal") {
      this.previouslyFocused = document.activeElement;
      this.bodyOverflow = document.body.style.overflow;
      this.root.hidden = false;
      this.hideLauncher();
      document.body.style.overflow = "hidden";
      document.addEventListener("keydown", this.boundKeydown);
      document.addEventListener("focusin", this.boundFocusIn);
      this.setState(initialState);
      if (!this.isPrivacyDialogOpen()) this.root.querySelector("[data-ii-surprise-close]")?.focus();
    }

    close(persist = false) {
      this.root.hidden = true;
      document.body.style.overflow = this.bodyOverflow || "";
      document.removeEventListener("keydown", this.boundKeydown);
      document.removeEventListener("focusin", this.boundFocusIn);
      if (persist) {
        writeStoredUntil(this.storageKey, this.root.dataset.frequencyDays || 30);
        const status = this.activeState === "success" ? "complete" : "dismissed";
        writeStoredStatus(this.statusKey, status);
        if (status === "dismissed") this.showLauncher();
      }
      this.previouslyFocused?.focus?.();
    }

    onSubmit(event) {
      const isTestMode = this.root.dataset.testMode === "true";
      const value = this.emailInput?.value.trim() || "";
      const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

      if (!valid) {
        event.preventDefault();
        this.emailInput?.setAttribute("aria-invalid", "true");
        if (this.emailError) this.emailError.textContent = "Enter a valid email address.";
        this.emailInput?.focus();
        return;
      }

      this.emailInput?.setAttribute("aria-invalid", "false");
      if (this.emailError) this.emailError.textContent = "";

      if (isTestMode) {
        event.preventDefault();
        this.applyFormResult("success");
        this.setState("success");
      }
    }

    applyFormResult(result, message = "") {
      if (result === "error") {
        this.emailInput?.setAttribute("aria-invalid", "true");
        if (this.emailError) {
          this.emailError.textContent = message || "We could not add that email. Check it and try again.";
        }
        this.setState("signup");
        return;
      }

      this.emailInput?.setAttribute("aria-invalid", "false");
      if (this.emailError) this.emailError.textContent = "";

      if (result === "already") {
        writeStoredUntil(this.storageKey, this.root.dataset.frequencyDays || 30);
        writeStoredStatus(this.statusKey, "complete");
        if (this.successHeading) this.successHeading.textContent = "You're already subscribed";
        if (this.successSupport) {
          this.successSupport.textContent = "There is no need to sign up again. We will not issue a duplicate welcome code.";
        }
        return;
      }

      if (this.successHeading) this.successHeading.textContent = "Thanks, you're all set";
      if (this.successSupport) {
        this.successSupport.textContent = "If you're eligible for a welcome code, we'll send it shortly. Already subscribed? We won't send a duplicate.";
      }
      writeStoredUntil(this.storageKey, this.root.dataset.frequencyDays || 30);
      writeStoredStatus(this.statusKey, "complete");
    }

    showLauncher() {
      if (!this.launcher || readStoredStatus(this.statusKey) === "complete") return;
      this.launcher.hidden = false;
      this.launcher.setAttribute("aria-expanded", "false");
    }

    hideLauncher() {
      if (!this.launcher) return;
      this.launcher.hidden = true;
      this.launcher.setAttribute("aria-expanded", "true");
    }

    isPrivacyDialogOpen() {
      return [...document.querySelectorAll(".shopify-pc__banner__dialog, .shopify-pc__prefs__dialog")].some((element) => element.getClientRects().length > 0 && window.getComputedStyle(element).visibility !== "hidden");
    }

    onFocusIn(event) {
      if (this.root.hidden || this.isPrivacyDialogOpen() || this.dialog.contains(event.target)) return;
      const target = [...this.dialog.querySelectorAll(focusableSelector)].find((element) => element.offsetParent !== null && !element.closest('[aria-hidden="true"]'));
      target?.focus();
    }

    onKeydown(event) {
      if (this.isPrivacyDialogOpen()) return;
      if (event.key === "Escape") {
        event.preventDefault();
        this.close(true);
        return;
      }

      if (event.key !== "Tab") return;

      const focusable = [...this.dialog.querySelectorAll(focusableSelector)].filter((element) => {
        return element.offsetParent !== null && !element.closest('[aria-hidden="true"]');
      });
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    failImage(image, panel) {
      if (image) image.hidden = true;
      panel?.classList.add("is-failed");
    }

    failLogo(logo) {
      logo.hidden = true;
      logo.closest(".ii-surprise__brand")?.classList.add("is-failed");
    }
  }

  document.querySelectorAll(SELECTOR).forEach((root) => {
    if (root.dataset.initialized === "true") return;
    root.dataset.initialized = "true";
    new NativeSurprisePopup(root).init();
  });
})();
